import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Instagram, Youtube, Music2 } from "lucide-react";
import { fetchCreatorBio } from "@/lib/queries";
import { resolveImage } from "@/lib/image-map";
import { SITE_URL } from "@/lib/utils";
import { RouteError } from "@/components/route-fallbacks";

export const Route = createFileRoute("/bio/$handle")({
  loader: ({ params }) => fetchCreatorBio(params.handle),
  errorComponent: RouteError,
  head: ({ params, loaderData }) => {
    const url = `${SITE_URL}/bio/${params.handle}`;
    const c = loaderData?.creator;
    const title = `@${params.handle}'s picks — Pluggz`;
    const image = c?.avatar_url ? resolveImage(c.avatar_url) : undefined;
    const desc = c?.bio?.trim() || `Shop @${params.handle}'s curated picks on Pluggz.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc.slice(0, 160) },
        { property: "og:title", content: title },
        { property: "og:description", content: desc.slice(0, 160) },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        ...(image ? [{ property: "og:image", content: image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        ...(image ? [{ name: "twitter:image", content: image }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BioPage,
});

function BioPage() {
  const { handle } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["creator-bio", handle],
    queryFn: () => fetchCreatorBio(handle),
  });

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-background px-4 py-10 text-center">
        <div className="mx-auto h-20 w-20 animate-pulse rounded-full bg-muted" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6 text-center">
        <div>
          <h1 className="font-serif text-2xl">This creator doesn't exist yet</h1>
          <Link to="/" className="mt-4 inline-block text-sm text-muted-foreground underline">
            Pluggz.
          </Link>
        </div>
      </div>
    );
  }

  const { creator, products } = data;

  return (
    <div className="min-h-dvh bg-background">
      <section className="px-4 pt-10 pb-6 text-center">
        {creator.avatar_url && (
          <img
            src={resolveImage(creator.avatar_url)}
            alt={creator.name}
            className="mx-auto h-20 w-20 rounded-full border-2 border-border object-cover"
          />
        )}
        <h1 className="mt-3 font-serif text-2xl">{creator.name}</h1>
        <p className="text-sm text-muted-foreground">@{creator.handle}</p>
        {creator.bio && (
          <p className="mx-auto mt-2 max-w-xs text-center text-sm text-muted-foreground">
            {creator.bio}
          </p>
        )}
        {(creator.instagram || creator.tiktok || creator.youtube) && (
          <div className="mt-4 flex items-center justify-center gap-4 text-muted-foreground">
            {creator.instagram && (
              <a href={creator.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
            )}
            {creator.tiktok && (
              <a href={creator.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <Music2 className="h-4 w-4" />
              </a>
            )}
            {creator.youtube && (
              <a href={creator.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <Youtube className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </section>

      <section className="px-4 pb-8">
        {products.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No products curated yet — check back soon
          </p>
        ) : (
          <div className="mx-auto max-w-md">
            {products.map((p) => (
              <article
                key={p.id}
                className="mb-3 overflow-hidden rounded-xl border border-border bg-card"
              >
                <img
                  src={resolveImage(p.image_key)}
                  alt={p.title}
                  loading="lazy"
                  className="aspect-[16/9] w-full object-cover"
                />
                <div className="p-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    {p.brand}
                  </p>
                  <h2 className="mt-1 font-serif text-base">{p.title}</h2>
                  {p.price ? (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {p.currency} {p.price}
                    </p>
                  ) : null}
                  <Link
                    to="/r/$handle/$productId"
                    params={{ handle: creator.handle, productId: p.id }}
                    className="mt-4 flex w-full items-center justify-center border border-foreground bg-foreground px-4 py-3 text-sm uppercase tracking-widest text-background"
                  >
                    Shop now →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <footer className="pb-10 text-center">
        <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground">
          Pluggz.
        </Link>
      </footer>
    </div>
  );
}